/**
 * This component is used to add dynamic titla and view all link to the page.
 *
 * @module views/components/DynamicTitle
 * @memberof -Common
 */
import './DynamicTitle.scss';

import React, { useCallback, useEffect, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { scrollToElement } from '@ulta/core/utils/scrollToElement/scrollToElement';

import { listenForElement } from '@ulta/utils/listenForElement/listenForElement';

/**
 * Represents a DynamicTitle component
 *
 * @method
 * @param {DynamicTitleProps} props - React properties passed from composition
 * @returns DynamicTitle
 */
export const DynamicTitle = function( props ){
  const { gridAlignment, viewAllAction, title, htmlTag = defaultProps.htmlTag, textStyle = defaultProps.textStyle, excludeFromDynamicGrid } = props;
  const [scrollTarget, setScrollTarget] = useState( null );
  let anchorId = viewAllAction?.anchorId;
  if( anchorId && anchorId.slice( 0, 1 ) !== '#' ){
    anchorId = `#${anchorId}`;
  }
  useEffect( () => {
    if( !anchorId ){
      return;
    }
    listenForElement(
      { onElementFound: setScrollTarget },
      { querySelector: anchorId }
    );
  }, [] );

  const scrollToAnchor = useCallback( () => {
    if( !scrollTarget ){
      return;
    }
    scrollToElement( { el: scrollTarget } );
  }, [scrollTarget] );

  if( !title ){
    return null;
  }

  return (
    <div className={ classNames( 'DynamicTitle', {
      'DynamicTitle--noGrid': excludeFromDynamicGrid
    } ) }
    >
      <div className='DynamicTitle__Grid'>
        <div className={ classNames( 'DynamicTitle__Content', {
          [`DynamicTitle__Content--${gridAlignment}`]: gridAlignment
        } )
        }
        >
          <Text htmlTag={ htmlTag }
            textStyle={ textStyle }
          >
            { title }
          </Text>

          { anchorId && (
            <Link_Huge
              withHover={ true }
              url={ anchorId }
              iconImage={ 'ArrowDown' }
              iconsize={ 's' }
              iconRight={ true }
              onClick={ scrollToAnchor }
              action={ viewAllAction }
            >
              { viewAllAction?.label }
            </Link_Huge>
          ) }
        </div>
      </div>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef DynamicTitleProps
 * @type {object}
 * @property {string} title - Display title
 * @property {string} htmlTag - Sets the HTML tag for the title
 * @property {string} textStyle - Sets the text styling
 * @property {string} gridAlignment - Set the gridAlignment position
 * @property {object} viewAllAction - This is the action object with action label and url.
 * @property {boolean} excludeFromDynamicGrid - conditionally adds grid
 */
export const propTypes = {
  /**
   * This is the title.
   */
  title: PropTypes.string,
  /**
   * Sets the HTML tag for the title
   */
  htmlTag: PropTypes.oneOf( [
    'p',
    'P',
    'span',
    'SPAN',
    'h1',
    'H1',
    'h2',
    'H2',
    'h3',
    'H3',
    'h4',
    'H4',
    'h5',
    'H5',
    'h6',
    'H6'
  ] ),
  /**
   * This provides the style needs to be applied
   */
  textStyle: PropTypes.string,
  /**
   * This provides the style needs to be applied for grid
   */
  gridAlignment: PropTypes.string,
  /**
   * This is the action with label and url.
   */
  viewAllAction: PropTypes.shape( {
    label: PropTypes.string,
    url: PropTypes.string,
    anchorId: PropTypes.string
  } ),
  /**
   * This provides the style needs to be applied for grid
   */
  excludeFromDynamicGrid: PropTypes.bool
};

/**
 * Default values for passed properties
 * @type object
 * @property {string} [ htmlTag = 'h1' ] - Sets the HTML tag for the title
 * @property {string} [ textStyle = 'title-5' ] - Sets the styling for text
 */
export const defaultProps = {
  htmlTag: 'h1',
  textStyle: 'title-5'
};

DynamicTitle.propTypes = propTypes;
DynamicTitle.defaultProps = defaultProps;

export default DynamicTitle;
